<script lang="ts">
	import Header from '$lib/components/Header.svelte';
	import Sidebar from './components/Sidebar.svelte';
	import { browser } from '$app/environment';
	// import { initializePubSubSubscriptions } from '$lib/server/youtubePubSub';
	import { page } from '$app/stores';
	import { loading } from '$lib/stores/loadingStore';
	import LiveStreamToast from '$lib/components/LiveStreamToast.svelte';
	import { setNewLiveChannel } from '$lib/stores/liveChannelStore';
	import '../app.css';
	import { SITE_NAME, SITE_URL } from '$lib/constants';
	import { Twitter, Video, Tv, Calendar, Radio, Signal, BarChart3 } from 'lucide-svelte';
	import MiniPlayer from '$lib/components/MiniPlayer.svelte';
	import { writable } from 'svelte/store';
	import { onMount, onDestroy } from 'svelte';
	import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
	import { initializeLiveChannelSubscription } from '$lib/services/liveChannelService';
	import LoadingBar from './components/LoadingBar.svelte';
	import { inject } from '@vercel/analytics';
	import { injectSpeedInsights } from "@vercel/speed-insights";
	import CategoryMenu from '$lib/components/CategoryMenu.svelte';
	import { Compass, Users, BookOpen } from 'lucide-svelte';
	import { activeTabStore } from '$lib/stores/tabStore';
	import { el } from 'date-fns/locale';
	import { currentVideoId, isPlaying, platform, twitchId, isMiniPlayerEnabled, isMiniPlayerActive } from '$lib/stores/miniPlayerStore';
	import { supabase } from '$lib/supabase';
	import { setAuthState, refreshSession, authStore } from '$lib/stores/authStore';
	import Popup from '$lib/components/Popup.svelte';
	import { _, locale, waitLocale } from 'svelte-i18n';
	import { fly, slide, scale } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import { isMobile as isMobileStore, mainScrollAreaHeight } from '$lib/stores';
	// import { Navigation } from './navigation';

	// Helper function to handle translations
	function translate(key: string, fallback: string): string {
		return $_ ? $_(key) : fallback;
	}

	// ナビゲーションメニューのラベルをリアクティブに更新
	$: topLabel = translate('common.navigation.top', 'トップ');
	$: followLabel = translate('common.navigation.follow', 'フォロー');
	$: scheduleLabel = translate('common.navigation.schedule', '番組表');
	$: liveRankingsLabel = translate('common.navigation.live_rankings', '配信ランキング');

	let isSidebarOpen = false;
	let popup: Popup | null = null;
	let isMobile: boolean;
	let savedMiniPlayerSetting: string | null = null;
	let showMobTransition = true;
	let fullWidthDemoSaving = true;
	let pageKey = '';

	function toggleSidebar() {
		isSidebarOpen = !isSidebarOpen;
	}

	function closeSidebar() {
		isSidebarOpen = false;
	}

	function handleMainClick() {
		if (isSidebarOpen) {
			closeSidebar();
		}
	}

	function updateMiniPlayer(videoId: string | null, playing: boolean) {
		currentVideoId.set(videoId);
		isPlaying.set(playing);
	}

	onMount(async () => {
		if (browser) {
			if (!import.meta.env.DEV) {
				inject();
				injectSpeedInsights();
				
				// Google Analytics の初期化
				const script = document.createElement('script');
				script.async = true;
				script.src = "https://www.googletagmanager.com/gtag/js?id=G-4T25LDZ9ZM";
				document.head.appendChild(script);

				// @ts-ignore
				window.dataLayer = window.dataLayer || [];
				function gtag(...args: any[]) {
					// @ts-ignore
					window.dataLayer.push(arguments);
				}
				gtag('js', new Date());
				gtag('config', 'G-4T25LDZ9ZM');
			}

			let unsubscribe: (() => void) | undefined;
			unsubscribe = initializeLiveChannelSubscription();

			const updateLayout = () => {
				isMobile = window.innerWidth < 768;
			};

			updateLayout();
			window.addEventListener('resize', updateLayout);

			// セッション状態が変わったときにポップアップで通知
			const unsubscribeAuth = authStore.subscribe(({ user }) => {
				if (user && popup) {
					// popup.show('ログインしました', 3000);
				}
			});

			// ウィンドウフォーカス時にセッション更新
			const handleWindowFocus = async () => {
				console.log('Window gained focus, refreshing session');
				await refreshSession();
			};

			window.addEventListener('focus', handleWindowFocus);

			// 表示状態変更時にセッション更新
			const handleVisibilityChange = async () => {
				if (document.visibilityState === 'visible') {
					console.log('Browser tab became visible, refreshing session');
					await refreshSession();
				}
			};

			document.addEventListener('visibilitychange', handleVisibilityChange);

			return () => {
				if (browser) {
					window.removeEventListener('resize', updateLayout);
					if (unsubscribe) unsubscribe();
					unsubscribeAuth();
					window.removeEventListener('focus', handleWindowFocus);
					document.removeEventListener('visibilitychange', handleVisibilityChange);
				}
			};
		}

		// ページ遷移時にMiniPlayerの状態をリセット
		const unsubscribe = currentVideoId.subscribe((videoId) => {
			if (!videoId) {
				// 動画ページ以外に遷移した場合、MiniPlayerを非表示にする
				updateMiniPlayer(null, false);
			}
		});

		return () => {
			unsubscribe();
		};
	});

	beforeNavigate(() => {
        loading.set(true);
    });
    afterNavigate(() => {
        loading.set(false);
    });

	$: isExploreTab = $page.url.pathname === '/' || $page.url.pathname.startsWith('/category/') || $page.url.pathname.startsWith('/rankings');
	$: isFollowTab = $page.url.pathname === '/follow';
	$: isArticlesTab = $page.url.pathname === '/articles';
	$: isRankingTab = $page.url.pathname === '/live-rankings';
	$: isScheduleTab = $page.url.pathname === '/schedule';
	$: isVideoPage = $page.url.pathname.startsWith('/video/');
	$: showCategoryMenu = !isVideoPage && isExploreTab;
	$: showHeader = !(isVideoPage && isMobile);
	$: showTabs = !isVideoPage;
	$: showBorder = isExploreTab || isFollowTab || isRankingTab || isScheduleTab;
	$: showFooter = !(isVideoPage && isMobile);

	$: path = $page.url.pathname;
	$: title = $page.data.title || (path === '/' ? `${SITE_NAME} - ${$_('common.site_description')}` : SITE_NAME);
	$: description = $_('meta.description') || 'VTGridは、VTuberの配信情報をリアルタイムで追跡し、配信中、配信予定、アーカイブを一目で把握できるプラットフォームです。';

	let exploreTabWidth = 0;
	let followTabWidth = 0;
	let articlesTabWidth = 0;
	let scheduleTabWidth = 0;
	let rankingTabWidth = 0;

	function setTabWidth(node: HTMLElement, tabName: string) {
		if (tabName === 'explore') {
			exploreTabWidth = node.offsetWidth;
		} else if (tabName === 'follow') {
			followTabWidth = node.offsetWidth;
		} else if (tabName === 'schedule') {
			scheduleTabWidth = node.offsetWidth;
		} else if (tabName === 'live-rankings') {
			rankingTabWidth = node.offsetWidth;
		} else if (tabName === 'schedule') {
			scheduleTabWidth = node.offsetWidth;
		}
	}

	function navigateToTab(tab: 'explore' | 'follow' | 'articles' | 'live-rankings' | 'schedule') {
		if (tab === 'explore') {
			goto('/');
		} else if (tab === 'follow') {
			goto('/follow');
		} else if (tab === 'articles') {
			goto('/articles');
		} else if (tab === 'live-rankings') {
			goto('/live-rankings');
		} else if (tab === 'schedule') {
			goto('/schedule');
		}
	}

	$: segments = $page.url.pathname.split('/').filter(segment => segment !== '');
	$: breadcrumbs = segments.map((segment, index) => {
		return {
			label: segment,
			href: '/' + segments.slice(0, index + 1).join('/'),
		};
	});

	// カスタムトランジション関数
	const scaleAndFade = (node, { delay = 0, duration = 400 }) => {
		return {
			delay,
			duration,
			css: (t) => {
				const scale = 0.5 + t * 0.5;
				return `
					transform: scale(${scale});
					opacity: ${t};
				`;
			}
		};
	};
</script>

<svelte:head>
  <meta name="msvalidate.01" content="0F76E13011569A0ED474DC967E36DBB3" />
  <meta name="application-name" content="VTGrid" />
  <!-- Bingのファビコン関連メタタグ -->
  <meta name="msapplication-TileColor" content="#000000" />
  <meta name="msapplication-TileImage" content="{SITE_URL}/og-image.png" />
  <meta name="msapplication-config" content="{SITE_URL}/browserconfig.xml" />
  
  <!-- <meta name="description" content={description} /> -->
  <!-- <meta name="keywords" content="VTuber, バーチャルYouTuber, 動画, ライブ配信, YouTube, ホロライブ, にじさんじ, ぶいすぽっ！" /> -->
  
  <meta property="og:title" content={title} />
  <!-- <meta property="og:description" content={description} /> -->
  <meta property="og:type" content={path === '/' ? 'website' : 'article'} />
  <meta property="og:url" content={`${SITE_URL}${path}`} />
  <meta property="og:image" content="https://vt-grid.com/og-image.png" />  
  <link rel="canonical" href={`${SITE_URL}${path}`} />
  
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content={title} />
  <!-- <meta name="twitter:description" content={description} /> -->
  <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
  {@html `
  <script>
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
  </script>
  `}
  <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1653216533599284"
  crossorigin="anonymous"></script>
</svelte:head>

<div class="flex flex-col min-h-screen bg-gray-100 dark:bg-[#1c1c1c] text-gray-800 dark:text-white">
	<LoadingBar />
	{#if showHeader}
		<Header {isSidebarOpen} {toggleSidebar} />
	{/if}
	<div class="flex flex-1 overflow-hidden">
		<Sidebar {isSidebarOpen} {toggleSidebar} />
		<main 
			class="flex-1 overflow-y-auto w-full"
			class:mt-14={showHeader}
			class:p-4={!isVideoPage}
			class:p-0={isVideoPage}
			class:md:pl-16={!isSidebarOpen}
			class:md:pl-64={isSidebarOpen}
			on:click={handleMainClick}
		>
			{#if showTabs}
				<div class="mb-6 border-b border-gray-200 dark:border-gray-700">
					<nav class="-mb-px flex relative" aria-label="Tabs">
						<button
							class="py-2 px-2 font-medium text-sm transition-colors duration-200 ease-in-out {isExploreTab ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}"
							on:click={() => navigateToTab('explore')}
							use:setTabWidth={'explore'}
						>
							<div class="flex items-center">
								{topLabel}
							</div>
						</button>
						<button
							class="py-2 px-2 font-medium text-sm transition-colors duration-200 ease-in-out {isFollowTab ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}"
							on:click={() => navigateToTab('follow')}
							use:setTabWidth={'follow'}
						>
							<div class="flex items-center">
								{followLabel}
							</div>
						</button>
						<button
							class="py-2 px-2 font-medium text-sm transition-colors duration-200 ease-in-out {isScheduleTab ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}"
							on:click={() => navigateToTab('schedule')}
							use:setTabWidth={'schedule'}
						>
							<div class="flex items-center">
								<Calendar size={20} class="mr-2" />
								{scheduleLabel}
							</div>
						</button>
						<button
							class="py-2 px-2 font-medium text-sm transition-all duration-200 ease-in-out {isRankingTab ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}"
							on:click={() => navigateToTab('live-rankings')}
							use:setTabWidth={'live-rankings'}
						>
							<div class="flex items-center">
								<BarChart3 
									size={20} 
									strokeWidth={2.5} 
									class="mr-2" 
								/>
								{liveRankingsLabel}
							</div>
						</button>
						{#if showBorder}
							<div 
								class="absolute bottom-0 left-0 h-0.5 bg-gray-900 dark:bg-white transition-all duration-300 ease-in-out" 
								style="width: {isRankingTab ? rankingTabWidth : isFollowTab ? followTabWidth : isScheduleTab ? scheduleTabWidth : exploreTabWidth}px; transform: translateX({isRankingTab ? exploreTabWidth + followTabWidth + scheduleTabWidth : isScheduleTab ? exploreTabWidth + followTabWidth : isFollowTab ? exploreTabWidth : 0}px);"
							></div>
						{/if}
					</nav>
				</div>
			{/if}

			{#if showCategoryMenu}
				<CategoryMenu />
			{/if}
<!-- 
			<nav class="breadcrumb my-4 text-gray-600 dark:text-gray-400">
				<ul>
					<li><a href="/">Home</a></li>
					{#each breadcrumbs as breadcrumb}
						<li><a href={breadcrumb.href}>{breadcrumb.label}</a></li>
					{/each}
				</ul>
			</nav> -->

			<slot />
		</main>
	</div>

	{#if showFooter && !isVideoPage}
		<footer class="bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white py-16 mt-auto shadow-xl border-t border-white/[0.08] relative overflow-hidden">
			<!-- 背景エフェクト -->
			<div class="absolute inset-0 z-0">
				<div class="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
				<div class="absolute -top-[50%] -left-[50%] w-[200%] h-[200%] bg-[radial-gradient(circle_at_center,rgba(120,50,255,0.02)_0,transparent_50%)]"></div>
				<div class="absolute -top-[80%] -right-[30%] w-[80%] h-[180%] bg-[radial-gradient(circle_at_center,rgba(220,100,255,0.015)_0,transparent_60%)]"></div>
			</div>
			
			<div class="container mx-auto px-4 relative z-10 transition-all duration-300 ease-in-out"
				 class:md:pl-16={!isSidebarOpen}
				 class:md:pl-64={isSidebarOpen}>
				
				<!-- ロゴとコピーライト -->
				<div class="flex flex-col items-center justify-center mb-12">
					<a href="/" class="text-3xl md:text-4xl font-bold text-white hover:opacity-90 transition-opacity duration-300 modern-font flex items-center mb-4">
						<span class="bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-500 bg-clip-text text-transparent font-extrabold tracking-tight">VT</span><span class="font-medium tracking-wide text-white/90">Grid</span>
					</a>
					<p class="text-sm text-white/50 max-w-xl text-center leading-relaxed">
						{$locale ? $_('footer.about_service') : 'VTuberやストリーマーのライブ配信とアーカイブ動画を簡単に見つけられるサービスです。YouTubeとTwitchに対応しています。'}
					</p>
				</div>
				
				<!-- メインコンテンツ -->
				<div class="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
					<!-- サービス -->
					<div class="flex flex-col items-center md:items-start">
						<h3 class="text-sm font-bold uppercase tracking-widest mb-5 text-white/70">{$_('footer.service')}</h3>
						<ul class="space-y-3">
							<li>
								<a href="/" class="text-sm text-white/60 hover:text-white transition-colors duration-200">{$_('footer.top_page')}</a>
							</li>
						</ul>
					</div>
					
					<!-- リンクセクション -->
					<div class="flex flex-col items-center md:items-start">
						<h3 class="text-sm font-bold uppercase tracking-widest mb-5 text-white/70">リンク</h3>
						<ul class="space-y-3">
							<li><a href="/about" class="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-1 hover:gap-2">
								<span>{$_('footer.links.about')}</span>
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-hover:opacity-100">
									<path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>
								</svg>
							</a></li>
							<li><a href="/terms" class="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-1 hover:gap-2">
								<span>{$_('footer.links.terms')}</span>
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-hover:opacity-100">
									<path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>
								</svg>
							</a></li>
							<li><a href="/privacy" class="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-1 hover:gap-2">
								<span>{$_('footer.links.privacy')}</span>
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-hover:opacity-100">
									<path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>
								</svg>
							</a></li>
						</ul>
					</div>
					
					<!-- ソーシャルメディア -->
					<div class="flex flex-col items-center md:items-start">
						<h3 class="text-sm font-bold uppercase tracking-widest mb-5 text-white/70">{$_('footer.follow_us')}</h3>
						<div class="flex gap-5">
							<a href="https://twitter.com/vtgrid" target="_blank" rel="noopener noreferrer" 
							   class="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-500/30 hover:scale-110 transition-all duration-300 group">
								<Twitter class="w-4 h-4 text-white/70 group-hover:text-white" />
							</a>
							<!-- 他のSNSアイコンを追加する場合はここに追加 -->
						</div>
					</div>
					
					<!-- クレジット部分 -->
					<div class="flex flex-col items-center md:items-start">
						<a 
							href="https://twitter.com/khrtz" 
							target="_blank" 
							rel="noopener noreferrer" 
							class="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-2 group"
						>
							<div class="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
								<Twitter class="w-3 h-3 text-white/70 group-hover:text-blue-400" />
							</div>
							<span class="flex items-center gap-1">
								<span class="text-white/60 group-hover:text-white/80">Created by</span>
								<span class="font-medium text-white/80 group-hover:text-white">khrtz</span>
							</span>
						</a>
					</div>
				</div>
				
				<!-- コピーライト -->
				<div class="mt-16 pt-8 border-t border-white/[0.08] text-center">
					<p class="text-xs text-white/40">&copy; {new Date().getFullYear()} <span class="text-white/60 font-medium">VTGrid</span>. {$locale ? $_('footer.all_rights_reserved') : 'All rights reserved.'}</p>
				</div>
			</div>
		</footer>
	{/if}
</div>

<!-- オーバーレイ（モバイル用） -->
{#if isSidebarOpen}
	<div 
		class="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden" 
		on:click={closeSidebar}
	></div>
{/if}


<!-- PCのミニプレイヤー -->
{#if browser && !isMobile && $currentVideoId && !isVideoPage && $isMiniPlayerEnabled && $isMiniPlayerActive}
    <div 
        class="fixed bottom-4 right-4 z-50 w-[338px] shadow-xl rounded-xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-zinc-900"
        role="complementary"
        aria-label="ミニプレイヤー"
    >
        <!-- ミニプレイヤーヘッダー -->
        <div 
            class="px-4 py-2.5 flex items-center justify-between border-b border-black/[0.08] dark:border-white/10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl"
            role="toolbar"
            aria-label="ミニプレイヤーコントロール"
        >
            <div class="flex items-center gap-3">
                <button 
                    class="text-black/60 dark:text-white/70 hover:text-black dark:hover:text-white p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 active:bg-black/10 dark:active:bg-white/20 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-black outline-none"
                    on:click={() => isMiniPlayerActive.set(false)}
                    aria-label="ミニプレイヤーを閉じる"
                    title="ミニプレイヤーを閉じる"
                >
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        class="w-4 h-4" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        stroke-width="2.5" 
                        stroke-linecap="round" 
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            <div class="flex items-center">
                <button 
                    class="text-black/60 dark:text-white/70 hover:text-black dark:hover:text-white p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 active:bg-black/10 dark:active:bg-white/20 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-black outline-none"
                    on:click={() => goto(`/video/${$currentVideoId}`)}
                    aria-label="動画ページを開く"
                    title="動画ページを開く"
                >
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        class="w-4 h-4" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        stroke-width="2.5" 
                        stroke-linecap="round" 
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                </button>
            </div>
        </div>

        <!-- プレイヤー本体 -->
        <div class="h-[190px]">
            {#if $platform === 'twitch'}
                <iframe
                    src="https://player.twitch.tv/?video={$twitchId}&parent=vt-grid.com&autoplay=true"
                    frameborder="0"
                    allowfullscreen
                    scrolling="no"
                    class="w-full h-full"
                    title="Twitchプレイヤー"
                ></iframe>
            {:else}
                <iframe
                    src="https://www.youtube.com/embed/{$currentVideoId}?autoplay=1&controls=1"
                    title="YouTubeプレイヤー"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    class="w-full h-full"
                ></iframe>
            {/if}
        </div>
    </div>
{/if}

<!-- モバイルのミニプレイヤー -->
{#if browser && $currentVideoId && isMobile && (isVideoPage || ($isMiniPlayerEnabled && $isMiniPlayerActive))}
    <MiniPlayer />
{/if}


<LiveStreamToast />

<!-- テスト用ボタンを追加 -->
<!-- <div class="fixed bottom-4 left-4 space-y-2">
	<button
		class="block w-full bg-blue-500 text-white px-4 py-2 rounded"
		on:click={testSingleLiveChannelToast}
	>
		単一トースト表示テスト
	</button>
	<button
		class="block w-full bg-green-500 text-white px-4 py-2 rounded"
		on:click={testMultipleLiveChannelToasts}
	>
		複数トースト同時表示テスト
	</button>
</div> -->

<style>
	main {
		/* transition: padding-left 0.3s ease-in-out; */
	}

	.breadcrumb ul {
		display: flex;
		flex-wrap: wrap;
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.breadcrumb li:not(:last-child)::after {
		content: '/';
		margin: 0 5px;
	}
	
	.modern-font {
		font-family: 'Outfit', sans-serif;
		letter-spacing: 0.01em;
	}
</style>

<!-- @ts-ignore -->
<Popup bind:this={popup} />

<svg width="0" height="0">
	<defs>
		<linearGradient id="rankingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
			<stop offset="0%" style="stop-color: rgb(239, 68, 68);" />
			<stop offset="50%" style="stop-color: rgb(168, 85, 247);" />
			<stop offset="100%" style="stop-color: rgb(59, 130, 246);" />
		</linearGradient>
	</defs>
</svg>
